/**
 * Preview panel showing change impact before applying.
 * Extracted from InvoiceManager.
 */

import styles from './invoiceManagerStyles';

export interface InvoicePreviewPanelProps {
  originalTotal: number;
  finalTotal: number;
  /** Voucher or collaboration discount already on the invoice, kept through the edit. */
  codeDiscount?: number;
  totalAdjustment: number;
  formatCurrency: (amount: number) => string;
  adjustmentReason: string;
  onSetAdjustmentReason: (v: string) => void;
  sendEmail: boolean;
  onSetSendEmail: (v: boolean) => void;
  loading: boolean;
  onApplyChanges: () => void;
  onCancel: () => void;
}

export default function InvoicePreviewPanel({
  originalTotal, finalTotal, totalAdjustment, formatCurrency, codeDiscount = 0,
  adjustmentReason, onSetAdjustmentReason,
  sendEmail, onSetSendEmail,
  loading, onApplyChanges, onCancel,
}: InvoicePreviewPanelProps) {
  return (
    <div style={styles.previewCard}>
      <h2 style={styles.previewTitle}>Preview Changes</h2>

      <div className="grid gap-lg" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
        <div>
          <h3 style={styles.previewSubtitle}>Invoice Changes:</h3>
          <div className="flex-between text-sm mb-xs">
            <span>Original Total:</span>
            <span>{formatCurrency(originalTotal)}</span>
          </div>
          {codeDiscount > 0 && (
            <div className="flex-between text-sm mb-xs" style={{ color: 'var(--color-text-light)' }}>
              <span>Voucher discount kept:</span>
              <span>-{formatCurrency(codeDiscount)}</span>
            </div>
          )}
          <div className="flex-between text-sm mb-xs" style={{ color: totalAdjustment > 0 ? 'var(--color-error)' : totalAdjustment < 0 ? 'var(--color-success)' : 'var(--color-text-light)' }}>
            <span>Total Adjustments:</span>
            <span>{totalAdjustment > 0 ? '-' : totalAdjustment < 0 ? '+' : ''}{formatCurrency(Math.abs(totalAdjustment))}</span>
          </div>
          <div className="flex-between fw-bold text-success mt-sm" style={{ fontSize: 'var(--text-3xl)', borderTop: '1px solid #e5e7eb', paddingTop: '0.5rem' }}>
            <span>New Total:</span>
            <span>{formatCurrency(finalTotal)}</span>
          </div>
        </div>

        <div>
          <h3 style={styles.previewSubtitle}>Impact:</h3>
          <p className="text-sm" style={{ color: 'var(--color-warning)' }}>
            ⚠️ Payment link will be regenerated with new amount
          </p>
          <p className="text-sm" style={{ color: 'var(--color-warning)' }}>
            ⚠️ Related bills will be updated/recalculated
          </p>
        </div>
      </div>

      {/* Reason field - REQUIRED */}
      <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'var(--color-warning-bg)', border: '1px solid #fed7aa', borderRadius: 'var(--radius)' }}>
        <label className="fw-600" style={{ ...styles.label, color: 'var(--color-warning-text)' }}>
          Adjustment Reason (Required) *
        </label>
        <textarea
          value={adjustmentReason}
          onChange={(e) => onSetAdjustmentReason(e.target.value)}
          placeholder="e.g., Customer complaint, pricing error, goodwill gesture..."
          style={{ ...styles.textarea, borderColor: !adjustmentReason.trim() ? 'var(--color-warning)' : 'var(--color-border)' }}
          rows={2}
        />
        {!adjustmentReason.trim() && (
          <p className="text-sm text-error mt-sm">
            You must provide a reason before applying changes
          </p>
        )}
      </div>

      {/* Email option */}
      <div style={styles.emailOption}>
        <label className="flex items-start gap-md cursor-pointer">
          <input
            type="checkbox"
            checked={sendEmail}
            onChange={(e) => onSetSendEmail(e.target.checked)}
            style={styles.checkbox}
          />
          <div>
            <div className="fw-600">Send updated invoice email to customer</div>
            <div className="text-sm" style={{ color: 'var(--color-text-light)' }}>
              When unchecked: Updates Zoho, payment link, and bills WITHOUT sending email
            </div>
          </div>
        </label>
      </div>

      <div className="flex gap-md mt-lg">
        <button
          onClick={onApplyChanges}
          disabled={loading || !adjustmentReason.trim()}
          style={{
            ...styles.greenBtn,
            opacity: (!adjustmentReason.trim() || loading) ? 0.5 : 1,
            cursor: (!adjustmentReason.trim() || loading) ? 'not-allowed' : 'pointer'
          }}
        >
          {loading ? 'Applying...' : 'Apply Changes'}
        </button>
        <button onClick={onCancel} style={styles.grayBtn}>
          Cancel
        </button>
      </div>
    </div>
  );
}
